import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import Loading from '../../../common/loading/components/Loading';
import getFallbackUrl from '../../../utils/getFallbackUrl';
import userInfo from '../../../utils/userInfo';
import { quickSortType } from '../../../constants/quickSortConstants';
import AvailableTripResults from '../components/AvailableTripResults';
import type { SearchDateType, TripType } from '../models';
import { getTripRoutes } from '../utils';
import type { RebookEligibilityType } from '../../order-details/components/manage-trip/rebooking/models';
import { REBOOK_SEARCH_MORE_QUERY, REBOOK_SEARCH_QUERY } from './queries';

export interface ResultsGraphqlContainerProps {
  newSearchDates: SearchDateType;
  originalTrip: TripType;
}

const ResultsGraphqlContainer = ({
  newSearchDates,
  originalTrip,
}: ResultsGraphqlContainerProps) => {
  const [shouldFetchMoreResults, setShouldFetchMoreResults] = useState(false);

  const variables = useMemo(
    () => ({
      filters: [],
      routes: getTripRoutes(newSearchDates, originalTrip),
      sortTypeCode: quickSortType.RECOMMENDED,
    }),
    [newSearchDates, originalTrip],
  );

  const {
    data: searchData,
    fetchMore,
    loading: searchLoading,
  } = useQuery(REBOOK_SEARCH_QUERY, {
    variables,
    notifyOnNetworkStatusChange: true,
    onError: () => {
      const fallbackUrl = getFallbackUrl();
      if (fallbackUrl) {
        window.location.href = fallbackUrl;
      } else {
        throw new Error(
          `Invalid response while fetching rebookingSearch results: ${JSON.stringify(userInfo())}`,
        );
      }
    },
  });

  useEffect(() => {
    if (searchData && !searchLoading) {
      setShouldFetchMoreResults(false);
    }
  }, [searchData, searchLoading]);

  const handleFetchMoreFlights = useCallback(() => {
    const offset = searchData?.rebookingSearch?.flights.length || 0;

    fetchMore({
      query: REBOOK_SEARCH_MORE_QUERY,
      variables: {
        ...variables,
        offset,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        const prevFlights = prev.rebookingSearch?.flights;
        const newFlights = fetchMoreResult.rebookingSearch?.flights;

        return {
          ...prev,
          rebookingSearch: {
            ...prev.rebookingSearch!!,
            flights: [...(prevFlights || []), ...(newFlights || [])],
          },
        };
      },
    }).catch(console.error); // eslint-disable-line no-console
  }, [fetchMore, variables, searchData?.rebookingSearch?.flights.length]);

  useEffect(() => {
    if (shouldFetchMoreResults) {
      handleFetchMoreFlights();
      setShouldFetchMoreResults(false);
    }
  }, [shouldFetchMoreResults, handleFetchMoreFlights]);

  // TODO handle error
  if (searchLoading || !searchData?.rebookingSearch) {
    return <Loading />;
  }

  return (
    <AvailableTripResults
      rebookEligibilityType={searchData.rebookingSearch.rebookFlow as RebookEligibilityType}
      search={searchData.rebookingSearch}
      setFetchMoreResults={setShouldFetchMoreResults}
      shouldFetchMoreResults={shouldFetchMoreResults}
    />
  );
};

export default ResultsGraphqlContainer;
